import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { classNames, isNumber } from "../utils";

export interface TimelineScrubberProps {
  /** Current playback time in seconds. */
  time: number;
  /** Total duration in seconds. Null while metadata is loading; disables the slider. */
  duration: number | null;
  /** End of the contiguous buffered range containing the current time (for the buffer bar). */
  bufferAmount: number;
  /** Frames per second — used to derive the slider's resolution. */
  fps: number;
  /** Whether the player is currently playing — used to decide if we should resume after a scrub. */
  isPlaying: boolean;
  /** Seek the player. Fires on every scrub movement. */
  onSeek: (time: number) => void;
  /** Called when the user starts scrubbing. Orchestrator should pause the player. */
  onScrubStart: () => void;
  /** Called when the user releases. Orchestrator decides whether to resume based on isPlaying at start. */
  onScrubEnd: () => void;
  /** Optional id for the slider element. */
  id?: string;
}

/**
 * The bottom-bar scrubber. Drives playback time via Radix slider with custom
 * pointer-capture so the drag stays smooth even if the cursor leaves the
 * slider mid-drag. Includes a hover indicator (vertical line at the cursor)
 * and a buffer bar showing what's preloaded.
 *
 * Designed to be generic — only takes time/duration/buffer numbers and
 * fires callbacks. No player coupling.
 */
export function TimelineScrubber({
  time,
  duration,
  bufferAmount,
  fps,
  isPlaying,
  onSeek,
  onScrubStart,
  onScrubEnd,
  id,
}: TimelineScrubberProps) {
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [scrubbing, setScrubbing] = useState(false);

  // Refs mirror state so pointer handlers can read synchronously without
  // depending on the render cycle.
  const scrubbingRef = useRef(false);
  const dragPointerIdRef = useRef<number | null>(null);
  // Captured at scrub start so onScrubEnd can decide whether to resume —
  // the parent's "isPlaying" might flip during the drag if scrubbing pauses
  // the player, and we want the *pre-scrub* state.
  const resumeAfterScrubRef = useRef(false);

  const safeDuration = isNumber(duration) ? duration : 0;
  const sliderDisabled = safeDuration <= 0;
  const sliderMax = sliderDisabled ? 1 : safeDuration;
  const sliderValue = sliderDisabled
    ? 0
    : Math.min(Math.max(0, time), safeDuration);
  // Step targets ~120 sub-frame increments to keep the keyboard arrow nudge
  // resolution above the frame rate, but no smaller than 1ms.
  const sliderStep = Math.max(1 / Math.max(1, fps * 120), 0.001);

  const startScrub = useCallback(() => {
    if (scrubbing || sliderDisabled) return;
    resumeAfterScrubRef.current = isPlaying;
    onScrubStart();
    setScrubbing(true);
    scrubbingRef.current = true;
  }, [scrubbing, sliderDisabled, isPlaying, onScrubStart]);

  const endScrub = useCallback(() => {
    if (!scrubbing) return;
    setScrubbing(false);
    scrubbingRef.current = false;
    dragPointerIdRef.current = null;
    if (resumeAfterScrubRef.current) onScrubEnd();
    resumeAfterScrubRef.current = false;
  }, [scrubbing, onScrubEnd]);

  const handleValueChange = (values: number[]) => {
    if (sliderDisabled) return;
    const next = values?.[0];
    if (typeof next !== "number" || !isFinite(next)) return;
    onSeek(next);
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (sliderDisabled || e.button === 2) return;
    startScrub();
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      dragPointerIdRef.current = e.pointerId;
    } catch {}
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width > 0) {
      const ratio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      onSeek(ratio * safeDuration);
    }
    try {
      e.stopPropagation();
    } catch {}
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    // Hover indicator follows the cursor regardless of scrub state.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (rect.width > 0) {
      const clamped = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      setHoverX(clamped);
    }

    if (!scrubbingRef.current) return;
    if (
      dragPointerIdRef.current != null &&
      e.pointerId !== dragPointerIdRef.current
    )
      return;
    if (rect.width > 0 && isNumber(duration)) {
      const ratio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      onSeek(ratio * duration);
    }
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    if (
      dragPointerIdRef.current != null &&
      e.pointerId !== dragPointerIdRef.current
    )
      return;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {}
    endScrub();
  };

  const expanded = hoverX != null || scrubbing;

  return (
    <div className="relative h-3">
      <SliderPrimitive.Root
        aria-label="Timeline scrubber"
        value={[sliderValue]}
        min={0}
        max={sliderMax}
        step={sliderStep}
        onValueChange={handleValueChange}
        disabled={sliderDisabled}
        id={id}
        onPointerMoveCapture={handlePointerMove}
        onPointerLeave={() => setHoverX(null)}
        onClick={(e) => {
          try {
            e.stopPropagation();
          } catch {}
        }}
        onPointerDownCapture={handlePointerDown}
        onPointerUpCapture={handlePointerUp}
        onPointerCancelCapture={handlePointerUp}
        className={classNames(
          "absolute inset-x-0 -top-1 -bottom-1 flex h-5 w-full touch-none select-none items-center",
          sliderDisabled && "opacity-50",
          "cursor-pointer",
        )}
      >
        <SliderPrimitive.Track
          className={classNames(
            "relative grow overflow-hidden rounded-full bg-[#DFE1E4] transition-[height] duration-200 ease-in-out",
            expanded ? "h-2" : "h-1",
          )}
        >
          <div
            style={{
              width:
                bufferAmount && duration
                  ? `${(bufferAmount / duration) * 100}%`
                  : undefined,
            }}
            className="absolute inset-y-0 left-0 bg-[#71717A]/30"
          />
          <SliderPrimitive.Range className="absolute inset-y-0 bg-[#6526DB]" />
          {expanded && (
            <div
              style={{
                left:
                  hoverX != null
                    ? `${hoverX}px`
                    : `${duration ? Math.min(100, Math.max(0, (Math.max(0, time) / duration) * 100)) : 0}%`,
              }}
              className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-[#282A30]/30"
            />
          )}
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className={classNames(
            "block rounded-full bg-[#282A30] shadow-sm transition-[width,height] focus:outline-none",
            expanded ? "h-2.5 w-1" : "h-1.5 w-0.5",
          )}
        />
      </SliderPrimitive.Root>
    </div>
  );
}
